import React, { useEffect, useRef, useState } from 'react';
import { Typography } from '@mui/material';
import LRSMarkdown from './LRSMarkdown';

interface StreamingTextProps {
    content: string;
}

const ANIMATION_MS = 400;

export const StreamingText: React.FC<StreamingTextProps> = ({ content }) => {
    const [lastContentLength, setLastContentLength] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        // Detect new content
        if (content.length > lastContentLength) {
            setIsAnimating(true);
            setLastContentLength(content.length);

            // Clear any existing timeout
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            // Stop animating after animation completes
            timeoutRef.current = setTimeout(() => {
                setIsAnimating(false);
            }, ANIMATION_MS);
        } else if (content.length < lastContentLength) {
            // Content was reset
            setLastContentLength(content.length);
            setIsAnimating(false);
        }

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [content, lastContentLength]);

    // Always render as markdown, apply animation when new content detected
    return (
        <>
            <style>{`
              @keyframes fadeInText { from { opacity:0; } to { opacity:1; } }
              .streaming-content-animating { animation: fadeInText .4s ease-in; }
              @media (prefers-reduced-motion: reduce) { .streaming-content-animating { animation: none; opacity:1; } }
            `}</style>
            <span className={isAnimating ? 'streaming-content-animating' : ''}>
                <Typography>
                    <LRSMarkdown>{content}</LRSMarkdown>
                </Typography>
            </span>
        </>
    );
};

export default StreamingText;
